import {
  SampleAssembler,
  decodeAthenaNotification,
  estimateDeviceClock,
  isAthenaDataCharacteristic,
  specForTag,
  windowedClock,
  type DeviceClockObservation,
  type SignalKind,
} from "@musejs/core";
import { readSession } from "@musejs/recording";

export interface SignalSummary {
  readonly signal: SignalKind;
  readonly channels: number;
  readonly samples: number;
  readonly measuredRate: number;
  readonly nominalRate: number;
  readonly dropped: number;
}

export interface InspectReport {
  readonly name: string;
  readonly device: string;
  readonly model: string;
  readonly transport: string;
  readonly mtu: string;
  readonly presetRequested: string;
  readonly frames: number;
  readonly durationSeconds: number;
  readonly integrityOk: boolean;
  readonly markers: number;
  readonly packets: number;
  readonly anomalies: number;
  readonly clockFamily: string;
  readonly clockRateHz: number;
  readonly signals: readonly SignalSummary[];
  readonly tags: readonly { readonly tag: number; readonly label: string; readonly count: number }[];
}

/**
 * Decodes a `.musez` file and reports what is in it.
 *
 * The browser equivalent of `musejs inspect`. Everything is derived by
 * re-decoding the raw frames, because that is all a session stores — which
 * means this report reflects the decoder this page was built with, not the one
 * that made the recording.
 */
export function inspectSession(name: string, bytes: Uint8Array): InspectReport {
  const session = readSession(bytes, { requireIntegrity: false });
  const integrity = session.verifyIntegrity();

  const assembler = new SampleAssembler({ createClock: () => windowedClock() });
  const observations: DeviceClockObservation[] = [];
  const tags = new Map<number, number>();
  const perSignal = new Map<
    SignalKind,
    { channels: number; samples: number; dropped: number; first: number; last: number; nominal: number }
  >();
  let packets = 0;
  let anomalies = 0;

  for (const frame of session.frames()) {
    if (!isAthenaDataCharacteristic(frame.characteristic)) continue;
    const result = decodeAthenaNotification(frame.payload);
    anomalies += result.anomalies.length;

    for (const packet of result.packets) {
      packets++;
      observations.push({ deviceTicks: packet.deviceTicks, hostTime: frame.arrivalTime });
      for (const subpacket of packet.subpackets) {
        tags.set(subpacket.tag, (tags.get(subpacket.tag) ?? 0) + 1);
      }
      for (const chunk of assembler.accept(packet, frame.arrivalTime)) {
        let entry = perSignal.get(chunk.signal);
        if (entry === undefined) {
          entry = {
            channels: chunk.channelNames.length,
            samples: 0,
            dropped: 0,
            first: chunk.timestamps[0] as number,
            last: chunk.timestamps[0] as number,
            nominal: chunk.sampleRate,
          };
          perSignal.set(chunk.signal, entry);
        }
        entry.samples += chunk.timestamps.length;
        entry.dropped += chunk.dropped;
        entry.last = chunk.timestamps[chunk.timestamps.length - 1] as number;
      }
    }
  }

  const clock = estimateDeviceClock(observations);

  const signals: SignalSummary[] = [...perSignal].map(([signal, entry]) => {
    const spanSeconds = (entry.last - entry.first) / 1e6;
    return {
      signal,
      channels: entry.channels,
      samples: entry.samples,
      measuredRate: spanSeconds > 0 ? entry.samples / spanSeconds : 0,
      nominalRate: entry.nominal,
      dropped: entry.dropped,
    };
  });

  return {
    name,
    device: session.manifest.device.name ?? "unnamed",
    model: session.manifest.device.model ?? "unknown",
    transport: session.manifest.transport,
    mtu: session.manifest.device.mtu === null ? "not reported" : `${session.manifest.device.mtu} B`,
    presetRequested: session.manifest.preset.requested ?? "none",
    frames: session.frameCount,
    durationSeconds: session.durationUs / 1e6,
    integrityOk: integrity.ok,
    markers: session.events.length,
    packets,
    anomalies,
    clockFamily: clock.family,
    clockRateHz: clock.ticksPerSecond,
    signals: signals.toSorted((a, b) => b.samples - a.samples),
    tags: [...tags]
      .map(([tag, count]) => ({ tag, label: labelForTag(tag), count }))
      .toSorted((a, b) => b.count - a.count),
  };
}

function labelForTag(tag: number): string {
  const spec = specForTag(tag);
  if (spec === undefined) return "unknown";
  return spec.kind === "drlref" ? "DRL/REF" : spec.kind;
}
